import React, { FC, memo, useLayoutEffect, useState } from 'react';
import { User } from '../user.types';
import { useAppDispatch } from '../../../hooks/defaultHooks';
import { openModal } from '../../../store/slices/modalSlice';
import cls from './UserCardSubInfoBlock.module.scss';
import Button from '../../../UIComponents/Button';
import { MdLock, MdLockReset, MdManageAccounts } from 'react-icons/md';
import { BiArchiveIn, BiArchiveOut } from 'react-icons/bi';

type UserCardSubInfoBlockProps = {
  user: User;
  isActualUser: boolean;
};

const UserStatus = ({ blocked, archived }: { blocked: boolean; archived: boolean }) => {
  if (archived) {
    return <span className={`${cls.FieldValue} ${cls.FieldValueDeleted}`}>Archived</span>;
  } else if (blocked) {
    return <span className={`${cls.FieldValue} ${cls.FieldValueRed}`}>Blocked</span>;
  } else {
    return <span className={`${cls.FieldValue} ${cls.FieldValueGreen}`}>Active</span>;
  }
};

const UserCardSubInfoBlock: FC<UserCardSubInfoBlockProps> = memo((props) => {
  const { user, isActualUser } = props;
  const [name, setName] = useState<string>('Unknown Beaver');
  useLayoutEffect(() => {
    if (user.firstName && user.secondName) {
      setName(`${user.firstName} ${user.secondName}`);
    } else if (user.firstName && !user.secondName) {
      setName(`${user.firstName} Beaver`);
    } else if (!user.firstName && user.secondName) {
      setName(`Unknown ${user.secondName}`);
    } else {
      setName('Unknown Beaver');
    }
  }, [user.firstName, user.secondName]);
  const dispatch = useAppDispatch();
  const unBlockUserHandler = () =>
    dispatch(
      openModal({
        type: 'unLockUser',
        extra: {
          email: user?.email,
          id: user?.id,
          title: 'Unblock employee?',
          supportText: 'Are you sure you want to unblock',
          textInformation: name,
          subSupportText: 'Data will be restored.',
          actionButtonText: 'Unblock',
        },
      }),
    );
  const blockHandler = () =>
    dispatch(
      openModal({
        type: 'lockUser',
        extra: {
          email: user?.email,
          id: user?.id,
          title: 'Block employee?',
          supportText: 'Are you sure you want to block',
          textInformation: name,
          subSupportText: 'Blocked account can be restored, data will be saved.',
          actionButtonText: 'Block',
        },
      }),
    );
  const updateRoleHandler = () =>
    dispatch(
      openModal({
        type: 'updateRole',
        extra: {
          email: user?.email,
          id: user?.id,
          title: 'Change role?',
          supportText: 'Change employee role',
          textInformation: name,
          actionButtonText: 'Confirm',
          userRole: user.role,
        },
      }),
    );

  const deleteUserHandler = () =>
    dispatch(
      openModal({
        type: 'deleteUser',
        extra: {
          email: user?.email,
          id: user?.id,
          title: 'Archive employee?',
          supportText: 'Are you sure you want to archive',
          textInformation: name,
          subSupportText: 'Confirm archiving by entering employee email',
          actionButtonText: 'Archive',
        },
      }),
    );
  const restoreUserHandler = () =>
    dispatch(
      openModal({
        type: 'restoreUser',
        extra: {
          email: user?.email,
          id: user?.id,
          title: 'Restore employee?',
          supportText: 'Are you sure you want to restore',
          textInformation: name,
          subSupportText: 'Confirm restoration by entering employee email',
          actionButtonText: 'Restore',
        },
      }),
    );
  return (
    <div className={cls.CardSubInfo}>
      <div className={cls.Info}>
        <div className={cls.Property}>
          <span className={cls.FieldName}>Status</span>
          <UserStatus blocked={user?.blocked} archived={user?.archived} />
        </div>
        <div className={cls.Property}>
          <span className={cls.FieldName}>Role</span>
          <span className={cls.FieldValue}>{user.roleName}</span>
        </div>
      </div>
      {!isActualUser && (
        <div className={cls.Buttons}>
          <Button
            clickHandler={updateRoleHandler}
            Icon={<MdManageAccounts />}
            text="Change Role"
            className={cls.Button}
          />
          <Button
            clickHandler={user?.blocked ? unBlockUserHandler : blockHandler}
            Icon={user?.blocked ? <MdLockReset /> : <MdLock />}
            text={user?.blocked ? 'Unblock' : 'Block'}
            className={cls.Button}
          />
          <Button
            clickHandler={user?.archived ? restoreUserHandler : deleteUserHandler}
            Icon={user?.archived ? <BiArchiveOut /> : <BiArchiveIn />}
            text={user?.archived ? 'Restore' : 'Archive'}
            className={cls.Button}
          />
        </div>
      )}
    </div>
  );
});

export default UserCardSubInfoBlock;
